import { Box, Text, Flex, Grid, GridItem } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { getConcentrationConfigValueType } from '@finley-technologies/calc-agent';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  AssignmentSummaryAccordion,
  AssignmentSummaryAccordionRow,
  StatRow,
} from './SharedComponents';
import type { PortfolioTestStats } from './TestStats';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { ConcentrationConfigCards } from '~/packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryConcentrationLimits';
import {
  BorrowingBaseSummaryValueType,
  borrowingBaseSummaryFormatterMap,
  ExpandedConcentrationLimitCalculations,
  getNumberedSubRowLabel,
  groupMultiLpConcentrationKeysByLoanProducts,
} from '~/packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from '~/packages/common-frontend/colors';

type AssignmentSummaryConcentrationLimitRowProps = {
  label: string;
  calcTerminologiesLabel?: string;
  initialValue?: Util.Maybe<number>;
  proposedValue?: Util.Maybe<number>;
  valueType: BorrowingBaseSummaryValueType;
};
const AssignmentSummaryConcentrationLimitRow = ({
  label,
  calcTerminologiesLabel,
  initialValue,
  proposedValue,
  valueType,
}: AssignmentSummaryConcentrationLimitRowProps) => {
  const formattedInitialValue = borrowingBaseSummaryFormatterMap[valueType](
    initialValue,
  );
  const formattedProposedValue = borrowingBaseSummaryFormatterMap[valueType](
    proposedValue,
  );

  return (
    <>
      <GridItem colSpan={2} display="flex" alignItems="center">
        <Text ml="120px" color={colors.black[70]}>
          {label}
        </Text>
        {calcTerminologiesLabel && (
          <Box display="inline-block" ml="8px">
            <CalculationTerminologiesPopover
              termKeyLabel={calcTerminologiesLabel}
            />
          </Box>
        )}
      </GridItem>
      <GridItem colSpan={1}>
        <Text color={colors.black[70]} ml="8px">
          {formattedInitialValue}
        </Text>
      </GridItem>
      <GridItem colSpan={1}>
        <Text color={colors.black[70]} ml="5px">
          {formattedProposedValue}
        </Text>
      </GridItem>
    </>
  );
};

type ConcentrationLimitProps = {
  concentrationLimitIndex: number;
  concentrationLimit: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  initialConcentrationCalculations?: ExpandedConcentrationLimitCalculations;
  proposedConcentrationCalculations?: ExpandedConcentrationLimitCalculations;
  initialTotalEligibleExposureLessConcentrationKickouts: number;
  proposedTotalEligibleExposureLessConcentrationKickouts: number;
};
export const ConcentrationLimit = ({
  concentrationLimit,
  concentrationLimitIndex,
  initialConcentrationCalculations,
  proposedConcentrationCalculations,
  initialTotalEligibleExposureLessConcentrationKickouts,
  proposedTotalEligibleExposureLessConcentrationKickouts,
}: ConcentrationLimitProps) => {
  const concentrationLimitConfig =
    concentrationLimit._config_.concentrationLimitConfig;

  if (!concentrationLimitConfig) {
    return null;
  }

  const concentrationLimitValueType = getConcentrationConfigValueType(
    concentrationLimitConfig,
  );

  return (
    <AssignmentSummaryAccordion
      accordionButton={({ hasAccordionPanel, isExpanded }) => (
        <AssignmentSummaryAccordionRow
          key={`loan-product-concentration-limit-${concentrationLimitConfig.propertyKey}`}
          subLabel={getNumberedSubRowLabel(
            concentrationLimitIndex,
            concentrationLimitConfig.propertyKey,
          )}
          initialValue={
            initialConcentrationCalculations?.borrowingBaseReduction
          }
          proposedValue={
            proposedConcentrationCalculations?.borrowingBaseReduction
          }
          valueType="currency"
          hasAccordionPanel={hasAccordionPanel}
          isExpanded={isExpanded}
          isSecondLevelAccordion
          background="light"
          level={4}
        />
      )}
      accordionPanel={
        <Box mt="16px">
          <Box ml="120px">
            <ConcentrationConfigCards
              concentrationLimitConfig={concentrationLimit}
              historicalConcentrationLimit={
                proposedConcentrationCalculations?.concentrationLimit
              }
            />
          </Box>
          <Grid templateColumns="repeat(4, 1fr)" gap="16px" marginBottom="16px">
            <AssignmentSummaryConcentrationLimitRow
              label="Concentration Amount"
              calcTerminologiesLabel="Concentration Amount"
              initialValue={
                initialConcentrationCalculations?.concentrationAmount
              }
              proposedValue={
                proposedConcentrationCalculations?.concentrationAmount
              }
              valueType="currency"
            />
            <AssignmentSummaryConcentrationLimitRow
              label="Total Eligible Exposure Less Concentration Kickouts"
              calcTerminologiesLabel="Total Eligible Exposure Less Concentration Kickouts"
              initialValue={
                initialTotalEligibleExposureLessConcentrationKickouts -
                (initialConcentrationCalculations?.borrowingBaseReduction ?? 0)
              }
              proposedValue={
                proposedTotalEligibleExposureLessConcentrationKickouts -
                (proposedConcentrationCalculations?.borrowingBaseReduction ?? 0)
              }
              valueType="currency"
            />
            <AssignmentSummaryConcentrationLimitRow
              label="Concentration"
              calcTerminologiesLabel="Concentration"
              initialValue={initialConcentrationCalculations?.concentration}
              proposedValue={proposedConcentrationCalculations?.concentration}
              valueType={
                concentrationLimitValueType === 'number'
                  ? 'decimal'
                  : concentrationLimitValueType
              }
            />
            <AssignmentSummaryConcentrationLimitRow
              label="Concentration Limit Amount"
              initialValue={
                initialConcentrationCalculations?.concentrationLimitAmount
              }
              proposedValue={
                proposedConcentrationCalculations?.concentrationLimitAmount
              }
              valueType="currency"
            />
            <AssignmentSummaryConcentrationLimitRow
              label="Concentration Kickout"
              initialValue={
                initialConcentrationCalculations?.borrowingBaseReduction
              }
              proposedValue={
                proposedConcentrationCalculations?.borrowingBaseReduction
              }
              valueType="currency"
            />
          </Grid>
        </Box>
      }
    />
  );
};

type ConcentrationStatsProps = {
  initialStats: PortfolioTestStats;
  proposedStats: PortfolioTestStats;
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  creditFacilitySlugMap: Map<string, string>;
};
export const ConcentrationStats = ({
  initialStats,
  proposedStats,
  parsedCreditAgreements,
  creditFacilitySlugMap,
}: ConcentrationStatsProps) => {
  return (
    <Box mt="24px">
      <Flex
        borderBottom="1px solid"
        borderColor={colors.gray[200]}
        p="8px 0px 8px"
      >
        <Text fontWeight="bold">Concentration Limits</Text>
      </Flex>
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Total Concentration Kickout"
            calcTerminologiesLabel="Total Concentration Kickout"
            initial={
              <Text fontWeight="medium" textAlign="justify">
                {borrowingBaseSummaryFormatterMap.currency(
                  initialStats.aggregateConcentrationKickout,
                )}
              </Text>
            }
            proposed={
              <Text fontWeight="medium">
                {borrowingBaseSummaryFormatterMap.currency(
                  proposedStats.aggregateConcentrationKickout,
                )}
              </Text>
            }
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(parsedCreditAgreements, (creditAgreement, index) => {
              const {
                borrowingBaseMechanics: {
                  calcAgentMechanics: {
                    loanProductMechanics,
                    multiLoanProductConcentrationLimits,
                  },
                },
                creditFacilityId,
              } = creditAgreement;

              const multiLpConcentrationKeysByLoanProductsGrouping = groupMultiLpConcentrationKeysByLoanProducts(
                { multiLoanProductConcentrationLimits },
              );
              const multiLoanProductConcentrationLimitsMap = new Map<
                string,
                CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities
              >();
              multiLoanProductConcentrationLimits.forEach((limit) => {
                const propertyKey =
                  limit._config_.concentrationLimitConfig?.propertyKey;
                if (propertyKey) {
                  multiLoanProductConcentrationLimitsMap.set(
                    propertyKey,
                    limit,
                  );
                }
              });

              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`credit-facility-concentration-kickout-${creditFacilityId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={creditFacilitySlugMap.get(creditFacilityId)}
                        initialValue={
                          initialStats.creditFacilityTestStats[creditFacilityId]
                            .totalConcentrationKickout
                        }
                        proposedValue={
                          proposedStats.creditFacilityTestStats[
                            creditFacilityId
                          ].totalConcentrationKickout
                        }
                        valueType="currency"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        fullRowBackground
                        background="medium"
                        level={2}
                      />
                    )}
                    accordionPanel={
                      <Box>
                        {_.map(loanProductMechanics, (loanProduct) => {
                          let concentrationTestIndex = -1;
                          return (
                            <Box
                              mt="8px"
                              key={`loan-product-concentration-kickout-${loanProduct.entity.id}`}
                            >
                              <AssignmentSummaryAccordion
                                accordionButton={({
                                  hasAccordionPanel,
                                  isExpanded,
                                }) => (
                                  <AssignmentSummaryAccordionRow
                                    label={loanProduct.entity.name}
                                    initialValue={
                                      initialStats.creditFacilityTestStats[
                                        creditFacilityId
                                      ].concentrationKickoutByLoanProductId[
                                        loanProduct.entity.id
                                      ]
                                    }
                                    proposedValue={
                                      proposedStats.creditFacilityTestStats[
                                        creditFacilityId
                                      ].concentrationKickoutByLoanProductId[
                                        loanProduct.entity.id
                                      ]
                                    }
                                    valueType="currency"
                                    hasAccordionPanel={hasAccordionPanel}
                                    isExpanded={isExpanded}
                                    background="light"
                                    level={3}
                                  />
                                )}
                                accordionPanel={
                                  <Box>
                                    {_.map(
                                      loanProduct.concentrationLimits,
                                      (concentrationLimit) => {
                                        if (
                                          concentrationLimit._config_
                                            .concentrationLimitConfig
                                        ) {
                                          concentrationTestIndex += 1;
                                          const concentrationLimitConfig =
                                            concentrationLimit._config_
                                              .concentrationLimitConfig;
                                          return (
                                            <Box
                                              mt="16px"
                                              key={`loan-product-concentration-limit-${loanProduct.entity.id}-${concentrationLimitConfig.propertyKey}`}
                                            >
                                              <ConcentrationLimit
                                                concentrationLimit={
                                                  concentrationLimit
                                                }
                                                concentrationLimitIndex={
                                                  concentrationTestIndex
                                                }
                                                initialConcentrationCalculations={
                                                  initialStats
                                                    .creditFacilityTestStats[
                                                    creditFacilityId
                                                  ]
                                                    .concentrationLimitTestsByLoanProductId[
                                                    loanProduct.entity.id
                                                  ][
                                                    concentrationLimitConfig
                                                      .propertyKey
                                                  ]
                                                }
                                                proposedConcentrationCalculations={
                                                  proposedStats
                                                    .creditFacilityTestStats[
                                                    creditFacilityId
                                                  ]
                                                    .concentrationLimitTestsByLoanProductId[
                                                    loanProduct.entity.id
                                                  ][
                                                    concentrationLimitConfig
                                                      .propertyKey
                                                  ]
                                                }
                                                initialTotalEligibleExposureLessConcentrationKickouts={
                                                  initialStats
                                                    .creditFacilityTestStats[
                                                    creditFacilityId
                                                  ]
                                                    .totalExposureLessEligibilityKickoutsByLoanProductId[
                                                    loanProduct.entity.id
                                                  ]
                                                }
                                                proposedTotalEligibleExposureLessConcentrationKickouts={
                                                  proposedStats
                                                    .creditFacilityTestStats[
                                                    creditFacilityId
                                                  ]
                                                    .totalExposureLessEligibilityKickoutsByLoanProductId[
                                                    loanProduct.entity.id
                                                  ]
                                                }
                                              />
                                            </Box>
                                          );
                                        }
                                        return (
                                          <Box
                                            key={`loan-product-concentration-limit-${loanProduct.entity.id}-${concentrationLimit.value}`}
                                          />
                                        );
                                      },
                                    )}
                                  </Box>
                                }
                                defaultIsExpanded={
                                  loanProductMechanics.length === 1
                                }
                              />
                            </Box>
                          );
                        })}
                        {_.map(
                          multiLpConcentrationKeysByLoanProductsGrouping,
                          (multiLpConcentrationKeys, lpGroupingSlugsString) => {
                            let concentrationTestIndex = -1;
                            return (
                              <Box
                                mt="8px"
                                key={`multi-loan-product-concentration-kickout-${lpGroupingSlugsString}`}
                              >
                                <AssignmentSummaryAccordion
                                  accordionButton={({
                                    hasAccordionPanel,
                                    isExpanded,
                                  }) => (
                                    <AssignmentSummaryAccordionRow
                                      label={lpGroupingSlugsString}
                                      initialValue={
                                        initialStats.creditFacilityTestStats[
                                          creditFacilityId
                                        ].concentrationKickoutByLoanProductId[
                                          lpGroupingSlugsString
                                        ]
                                      }
                                      proposedValue={
                                        proposedStats.creditFacilityTestStats[
                                          creditFacilityId
                                        ].concentrationKickoutByLoanProductId[
                                          lpGroupingSlugsString
                                        ]
                                      }
                                      valueType="currency"
                                      hasAccordionPanel={hasAccordionPanel}
                                      isExpanded={isExpanded}
                                      background="light"
                                      level={3}
                                    />
                                  )}
                                  accordionPanel={
                                    <Box>
                                      {_.map(
                                        multiLpConcentrationKeys,
                                        (multiLpConcentrationKey) => {
                                          concentrationTestIndex += 1;
                                          const multiLoanProductConcentrationLimit = multiLoanProductConcentrationLimitsMap.get(
                                            multiLpConcentrationKey,
                                          );
                                          if (
                                            !multiLoanProductConcentrationLimit
                                          ) {
                                            return (
                                              <Box
                                                key={`multi-loan-product-concentration-limit-${lpGroupingSlugsString}-${concentrationTestIndex}`}
                                              />
                                            );
                                          }
                                          const initialTotalEligibleExposureLessConcentrationKickouts = _.sumBy(
                                            multiLoanProductConcentrationLimit.loanProducts,
                                            (loanProduct) => {
                                              return initialStats
                                                .creditFacilityTestStats[
                                                creditFacilityId
                                              ]
                                                .totalExposureLessEligibilityKickoutsByLoanProductId[
                                                loanProduct.entity.id
                                              ];
                                            },
                                          );
                                          const proposedTotalEligibleExposureLessConcentrationKickouts = _.sumBy(
                                            multiLoanProductConcentrationLimit.loanProducts,
                                            (loanProduct) => {
                                              return proposedStats
                                                .creditFacilityTestStats[
                                                creditFacilityId
                                              ]
                                                .totalExposureLessEligibilityKickoutsByLoanProductId[
                                                loanProduct.entity.id
                                              ];
                                            },
                                          );
                                          return (
                                            <Box
                                              mt="16px"
                                              key={`multi-loan-product-concentration-limit-${lpGroupingSlugsString}`}
                                            >
                                              <ConcentrationLimit
                                                concentrationLimit={
                                                  multiLoanProductConcentrationLimit
                                                }
                                                concentrationLimitIndex={
                                                  concentrationTestIndex
                                                }
                                                initialConcentrationCalculations={
                                                  initialStats
                                                    .creditFacilityTestStats[
                                                    creditFacilityId
                                                  ]
                                                    .concentrationLimitTestsByLoanProductId[
                                                    lpGroupingSlugsString
                                                  ][multiLpConcentrationKey]
                                                }
                                                proposedConcentrationCalculations={
                                                  proposedStats
                                                    .creditFacilityTestStats[
                                                    creditFacilityId
                                                  ]
                                                    .concentrationLimitTestsByLoanProductId[
                                                    lpGroupingSlugsString
                                                  ][multiLpConcentrationKey]
                                                }
                                                initialTotalEligibleExposureLessConcentrationKickouts={
                                                  initialTotalEligibleExposureLessConcentrationKickouts
                                                }
                                                proposedTotalEligibleExposureLessConcentrationKickouts={
                                                  proposedTotalEligibleExposureLessConcentrationKickouts
                                                }
                                              />
                                            </Box>
                                          );
                                        },
                                      )}
                                    </Box>
                                  }
                                  defaultIsExpanded={
                                    Object.keys(
                                      multiLpConcentrationKeysByLoanProductsGrouping,
                                    ).length === 1
                                  }
                                />
                              </Box>
                            );
                          },
                        )}
                      </Box>
                    }
                    defaultIsExpanded={parsedCreditAgreements.length === 1}
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Total Eligible Exposure Less Concentration Kickouts"
            calcTerminologiesLabel="Total Eligible Exposure Less Concentration Kickouts"
            initial={
              <Text fontWeight="medium" textAlign="justify">
                {borrowingBaseSummaryFormatterMap.currency(
                  initialStats.aggregateEligibleExposureLessConcentrationKickouts,
                )}
              </Text>
            }
            proposed={
              <Text fontWeight="medium">
                {borrowingBaseSummaryFormatterMap.currency(
                  proposedStats.aggregateEligibleExposureLessConcentrationKickouts,
                )}
              </Text>
            }
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(parsedCreditAgreements, (creditAgreement, index) => {
              const {
                borrowingBaseMechanics: {
                  calcAgentMechanics: { loanProductMechanics },
                },
                creditFacilityId,
              } = creditAgreement;

              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`credit-facility-eligible-exposure-less-kickouts-${creditFacilityId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={creditFacilitySlugMap.get(creditFacilityId)}
                        initialValue={
                          initialStats.creditFacilityTestStats[creditFacilityId]
                            .totalEligibleExposureLessConcentrationKickouts
                        }
                        proposedValue={
                          proposedStats.creditFacilityTestStats[
                            creditFacilityId
                          ].totalEligibleExposureLessConcentrationKickouts
                        }
                        valueType="currency"
                        fullRowBackground
                        background="medium"
                        level={2}
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                      />
                    )}
                    accordionPanel={
                      <Box>
                        {_.map(loanProductMechanics, (loanProduct) => {
                          return (
                            <Box
                              mt="8px"
                              key={`loan-product-eligible-exposure-less-kickouts-${loanProduct.entity.id}`}
                            >
                              <AssignmentSummaryAccordionRow
                                label={loanProduct.entity.name}
                                initialValue={
                                  initialStats.creditFacilityTestStats[
                                    creditFacilityId
                                  ]
                                    .totalEligibleExposureLessConcentrationKickoutsByLoanProductId[
                                    loanProduct.entity.id
                                  ]
                                }
                                proposedValue={
                                  proposedStats.creditFacilityTestStats[
                                    creditFacilityId
                                  ]
                                    .totalEligibleExposureLessConcentrationKickoutsByLoanProductId[
                                    loanProduct.entity.id
                                  ]
                                }
                                valueType="currency"
                                background="light"
                                level={3}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    }
                    defaultIsExpanded={parsedCreditAgreements.length === 1}
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
    </Box>
  );
};
